/**
 * THE PHASE NUMBERING RULE — DP103 ruling 1. One rule, in one place.
 *
 * `phase_number` COUNTS FROM 1 and is the phase's position in its own template.
 * `project.current_phase` holds a phase_number, not an array index.
 *
 * Before this, `PROJECT_TYPE_TEMPLATES` (backend/routes/phases.py) numbered every
 * built-in template from 0 while `DEFAULT_PROJECT_PHASES`, `/api/projects/by-phase`
 * and the two dashboards' filter rows counted from 1 — and the two admin template
 * editors renumbered from opposite ends (`PhaseTemplateEditor` from 0,
 * `AdminSettings` from 1), so whichever saved last decided a template's base.
 * Four of Chad's six real projects carried the 0-based numbering.
 *
 * Everything that CREATES or RENUMBERS phases calls `renumber()` from here.
 * Everything that RESOLVES a project's current phase calls `currentPhaseOf()`.
 * The mirror of this file on the server is backend/utils/phase_numbering.py.
 */
import { phaseShort } from './phaseShort';

export const FIRST_PHASE_NUMBER = 1;

/** The one renumbering. List order is authoritative; steps follow their phase. */
export const renumber = (phases) => (phases || []).map((phase, idx) => {
  const number = FIRST_PHASE_NUMBER + idx;
  const steps = phase?.steps;
  return {
    ...phase,
    phase_number: number,
    ...(Array.isArray(steps)
      ? { steps: steps.map((s) => (s && 'phase_number' in s ? { ...s, phase_number: number } : s)) }
      : {}),
  };
});

export const isFirstPhase = (phase) => phase?.phase_number === FIRST_PHASE_NUMBER;

/**
 * A project's current phase, as an object, or null.
 *
 * The steps are a GUIDE the user drives — ticked in any order, moved back and
 * forth — so `in_progress` is what the user last said, and it wins. There is no
 * array-index fallback: DP102 removed `phases[current_phase]` because a wrong
 * phase is worse than none.
 */
export const currentPhaseOf = (project) => {
  const phases = project?.phases || [];
  if (!phases.length) return null;
  return phases.find((ph) => ph.status === 'in_progress')
    || (Number.isFinite(project.current_phase)
      ? phases.find((ph) => ph.phase_number === project.current_phase)
      : null)
    || phases.find((ph) => ph.status !== 'completed')
    || null;
};

export const currentPhaseName = (project) => currentPhaseOf(project)?.name || null;

/** The cell a project belongs in. A project is never in two, and never in none. */
export const NO_PHASE_CELL = 'no-phase';
export const NO_PHASE_LABEL = 'Phase not set';

export const phaseCellKey = (project) => currentPhaseName(project) || NO_PHASE_CELL;

export const cellSlug = (key) => String(key).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/**
 * DP103 ruling 2 — THE STRIP READS THE PROJECTS' OWN PHASES.
 *
 * `/api/projects/by-phase` used to bucket `{1..6}` and drop anything else, so a
 * project in an eight-phase template's Client Onboarding (0) or Closeout (7) was
 * counted nowhere. Six fixed cells cannot represent an eight-phase project and a
 * two-phase project at the same time, so the cells are no longer fixed: they are
 * the phases the tenant's active projects are ACTUALLY in, in phase order, and
 * every project lands in exactly one.
 */
export const phaseCells = (projects) => {
  const cells = new Map();
  (projects || []).forEach((project) => {
    const phase = currentPhaseOf(project);
    const key = phase?.name || NO_PHASE_CELL;
    const existing = cells.get(key);
    const number = Number.isFinite(phase?.phase_number) ? phase.phase_number : null;
    if (existing) {
      existing.count += 1;
      if (number !== null && (existing.number === null || number < existing.number)) existing.number = number;
    } else {
      cells.set(key, {
        key,
        number,
        name: phase?.name || NO_PHASE_LABEL,
        short: phase?.name ? phaseShort(phase.name) : '—',
        count: 1,
      });
    }
  });
  return [...cells.values()].sort((a, b) => {
    if (a.key === NO_PHASE_CELL) return 1;
    if (b.key === NO_PHASE_CELL) return -1;
    if (a.number !== b.number) return (a.number ?? 1e9) - (b.number ?? 1e9);
    return a.name.localeCompare(b.name);
  });
};
